import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/accordion.html
 *
 * Outer 6px radius, 1px border-subtle. Items separated by bottom border (last
 * gets none). Header 10×14 padding, hover bg.subtle, chevron rotates 90° on
 * open. Body pad 4px 14px 14px 38px, fg.muted 12px 1.6 line-height.
 *
 * v3 port notes:
 * - Slots renamed from v2's `accordionAnatomy`: `container` → `item`,
 *   `button` → `itemTrigger`, `icon` → `itemIndicator`. v2's single `panel`
 *   part splits into two in v3: `itemContent` (the animated open/close
 *   wrapper — `overflow: hidden` plus expand/collapse animations, confirmed
 *   in the built-in recipe) and `itemBody` (a plain inner div meant to carry
 *   padding, kept out of the animated element so padding doesn't skew the
 *   height transition). This port keeps `itemContent` unstyled and puts all
 *   of v2's `panel` styling on `itemBody` — matches the built-in's own split.
 *   Call sites must therefore render `Accordion.ItemBody` inside
 *   `Accordion.ItemContent`; `MAccordionPanel` does that for them (see
 *   `MAccordionPanel.tsx`/`MSettingAccordionItem.tsx`).
 * - `_expanded` (v2 pseudo-prop) → `_open` in v3 (confirmed via the built-in
 *   recipe's own `itemIndicator._open`/`itemContent._open` usage — same
 *   rename already documented for Tabs/Menu's selected-state selectors).
 * - v2's top-level `baseStyle` param (unconditional regardless of variant)
 *   ported as this recipe's own `base` — v2's `variants.base` (the *named*
 *   variant selected by `defaultProps.variant: 'base'`) is a **separate,
 *   much richer** definition and is ported as `variants.variant.base`
 *   below; naming coincidence carried over from v2, not a v3 quirk. v2's
 *   `variants.default` was a genuinely empty object (opt-out of all
 *   variant-level styling, callers explicitly pass `variant="default"` to
 *   get a bare accordion) — kept empty here too.
 * - Confirmed via `createSystem`/`getSlotRecipeFn` (same technique as
 *   Card/Menu/Tabs/Tag/Table) that this recipe's own explicit `itemTrigger`
 *   padding in each variant already wins over the built-in `size` variant's
 *   own padding (this app's theme never defines its own `size` category,
 *   same call already made for Tag/Table) - no extra neutralization needed
 *   beyond what's written into each variant.
 */

// v2's CSS reset gave every `button` `cursor: pointer`; v3's preflight
// doesn't, so the trigger has to set it itself.
const sharedItemTrigger = {
  borderRadius: 'sm',
  cursor: 'pointer',
  _disabled: {
    cursor: 'not-allowed',
  },
  _focusVisible: {
    boxShadow: 'focus',
    outline: 'none',
  },
};

export const accordionTheme = defineSlotRecipe({
  slots: [
    'root',
    'item',
    'itemTrigger',
    'itemContent',
    'itemBody',
    'itemIndicator',
  ],
  base: {
    itemTrigger: sharedItemTrigger,
  },
  variants: {
    variant: {
      base: {
        item: {
          borderColor: 'border.subtle',
          borderWidth: '1px',
          borderRadius: 'md', // 6px
          bg: 'bg.surface',
        },
        itemBody: {
          pt: 1,
          pb: 4,
          px: 6,
          fontSize: 'xs',
          color: 'fg.muted',
          lineHeight: 1.6,
        },
        itemTrigger: {
          py: '10px',
          px: '14px',
          fontSize: 'sm',
          fontWeight: 500,
          color: 'fg.primary',
          _hover: { bg: 'bg.subtle' },
        },
        itemIndicator: {
          color: 'fg.muted',
          w: '12px',
          h: '12px',
        },
      },
      default: {},
      primary: {
        item: {
          borderColor: 'border.subtle',
          borderRadius: 'md',
          borderWidth: '1px',
        },
        itemBody: { p: 0, mx: 0 },
        itemTrigger: {
          py: 0,
          px: '4',
          minH: '56px',
          borderColor: 'border.subtle',
          borderBottomWidth: '1px',
          backgroundColor: 'bg.selected',
          _hover: { backgroundColor: 'tBlue.100' },
        },
      },
      outline: {
        root: {
          borderColor: 'border.subtle',
          borderRadius: 'md',
          borderWidth: '1px',
          bg: 'bg.surface',
        },
        itemBody: { p: 0, mx: 0 },
        itemTrigger: {
          py: 0,
          px: '4',
          minH: '56px',
          _open: {
            borderColor: 'border.subtle',
            borderBottomWidth: '1px',
          },
        },
      },
      bottomBorder: {
        itemBody: { p: 0, mx: 0 },
        itemTrigger: {
          py: 0,
          px: '4',
          minH: '56px',
          borderColor: 'border.subtle',
          borderBottomWidth: '1px',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'base',
  },
});

export default accordionTheme;
